import { format } from "date-fns";
import { useI18n } from "@/lib/i18n";
import { Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, CloudHail, Droplets, Wind, Sunrise, Sunset } from "lucide-react";
import { WEATHER_CODES, riskLevel, hailLevel } from "@/lib/params";

const codeIcon = (c) =>
  c === 0 ? Sun : c <= 2 ? CloudSun : c === 3 ? Cloud : c <= 48 ? CloudFog : c <= 57 ? CloudDrizzle
    : c <= 67 || (c >= 80 && c <= 82) ? CloudRain : c <= 77 || c === 85 || c === 86 ? CloudSnow : c >= 96 ? CloudHail : CloudLightning;

const DayRow = ({ d, i, lo, hi }) => {
  const { t, locale, lang } = useI18n();
  const Icon = codeIcon(d.weather_code);
  const left = ((d.temperature_2m_min - lo) / (hi - lo || 1)) * 100;
  const width = ((d.temperature_2m_max - d.temperature_2m_min) / (hi - lo || 1)) * 100;
  const road = riskLevel(d.road_max), hail = hailLevel(d.hail_max);
  return (
    <div data-testid={`week-day-${i}`} className="rounded-xl border border-white/5 bg-white/[0.03] p-3" style={{ animation: `slideR .35s ease-out ${i * 40}ms both` }}>
      <div className="flex items-center gap-3">
        <div className="w-16">
          <div className="font-heading text-lg font-bold capitalize text-white">{i === 0 ? t("Bugün") : format(new Date(d.time), "EEEE", { locale })}</div>
          <div className="font-mono text-[10px] text-slate-500">{format(new Date(d.time), "d MMM", { locale })}</div>
        </div>
        <Icon size={26} className="shrink-0 text-sky-300" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-slate-300">{t(WEATHER_CODES[d.weather_code] || "—")}</div>
          <div className="mt-1.5 flex items-center gap-2 font-mono text-xs">
            <span data-testid={`week-min-${i}`} className="w-7 text-right text-sky-300">{Math.round(d.temperature_2m_min)}°</span>
            <div className="relative h-1.5 flex-1 rounded-full bg-white/10">
              <div className="absolute h-full rounded-full" style={{ left: `${left}%`, width: `${Math.max(width, 4)}%`, background: "linear-gradient(90deg,#38bdf8,#f97316)" }} />
            </div>
            <span data-testid={`week-max-${i}`} className="w-7 text-orange-300">{Math.round(d.temperature_2m_max)}°</span>
          </div>
        </div>
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[11px] text-slate-400">
        <span className="flex items-center gap-1"><Droplets size={12} className="text-blue-400" />{d.precipitation_sum?.toFixed(1)} mm · {lang === "en" ? `${d.precipitation_probability_max ?? 0}%` : `%${d.precipitation_probability_max ?? 0}`}</span>
        <span className="flex items-center gap-1"><Wind size={12} className="text-cyan-400" />{Math.round(d.wind_speed_10m_max)} / {Math.round(d.wind_gusts_10m_max)} {t("km/sa")}</span>
        <span className="flex items-center gap-1"><Sunrise size={12} className="text-amber-300" />{d.sunrise?.slice(11)}</span>
        <span className="flex items-center gap-1"><Sunset size={12} className="text-orange-400" />{d.sunset?.slice(11)}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span data-testid={`week-road-${i}`} className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: road.color, background: `${road.color}1a` }}>{t("Yol")}: {t(road.label)}</span>
        {d.hail_max >= 10 && <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: hail.color, background: `${hail.color}1a` }}>{t(hail.label)}</span>}
        {d.visibility_min != null && d.visibility_min < 3000 && <span className="rounded-full bg-slate-400/10 px-2 py-0.5 text-[11px] font-semibold text-slate-200">{t("Sis")} · {(d.visibility_min / 1000).toFixed(1)} km</span>}
      </div>
    </div>
  );
};

export const WeekView = ({ days }) => {
  const lo = Math.min(...days.map((d) => d.temperature_2m_min)), hi = Math.max(...days.map((d) => d.temperature_2m_max));
  return (
    <div data-testid="week-view" className="space-y-2 p-4">
      {days.map((d, i) => <DayRow key={d.time} d={d} i={i} lo={lo} hi={hi} />)}
    </div>
  );
};
